import SeoHead from "@/components/SeoHead";
import FeaturePageLayout from "@/components/FeaturePageLayout";
import TypewriterDemo from "@/components/TypewriterDemo";
import { DollarSign, ShoppingBag, CreditCard, Code, PenTool, BarChart3, TrendingUp, Target, CheckCircle, Send } from "lucide-react";
import { SITE_URL } from "@/lib/siteConfig";

const SCENARIOS = [
  {
    text: "Processing Shopify order webhook — attributing to email campaign...",
    results: [
      { icon: <ShoppingBag size={14} className="text-purple-500" />, title: "Shopify webhook received", desc: "Order #8421 — $127.50 from sarah@example.com" },
      { icon: <Send size={14} className="text-blue-500" />, title: "Email attribution matched", desc: 'Subscriber clicked "Shop Now" in Campaign: Spring Collection Launch (2 hours ago)' },
      { icon: <DollarSign size={14} className="text-emerald-500" />, title: "Revenue recorded", desc: "$127.50 attributed to campaign. Lifetime email revenue for this subscriber: $412.00" },
      { icon: <CheckCircle size={14} className="text-emerald-500" />, title: "Dashboard updated", desc: "Campaign ROI: 4,200%. Total email revenue this month: $18,940." },
    ],
  },
  {
    text: "Stripe subscription payment processed — tracking recurring revenue...",
    results: [
      { icon: <CreditCard size={14} className="text-blue-500" />, title: "Stripe event received", desc: "invoice.payment_succeeded — $49/mo from Pro plan subscriber" },
      { icon: <Send size={14} className="text-coral" />, title: "Attribution chain", desc: "Originally converted via 'Free Trial Onboarding' automation, email #4" },
      { icon: <DollarSign size={14} className="text-emerald-500" />, title: "Recurring revenue tracked", desc: "$49 MRR attributed. Total automation revenue: $3,920/mo" },
      { icon: <CheckCircle size={14} className="text-emerald-500" />, title: "Revenue dashboard updated", desc: "Onboarding automation: $47,040 annual revenue. Best-performing flow." },
    ],
  },
];

export default function RevenueAttributionLandingPage() {
  return (
    <>
      <SeoHead
        title="Revenue Attribution — See Exactly How Much Each Email Earns | MoreMail"
        description="Track conversions and revenue from every email campaign. Automatic attribution from Shopify, Stripe, and custom integrations."
        canonical={`${SITE_URL}/revenue-attribution`}
        keywords="email revenue attribution, email ROI tracking, campaign revenue tracking, Shopify email revenue, email conversion tracking"
        ogTitle="Revenue Attribution — Track Every Dollar From Email | MoreMail"
        ogDescription="Connect Shopify, Stripe, or use the API. See exactly which campaigns and automations drive revenue."
        jsonLd={{ "@context": "https://schema.org", "@type": "SoftwareApplication", name: "Revenue Attribution", applicationCategory: "BusinessApplication", description: "Track revenue from every email campaign.", offers: { "@type": "Offer", price: "0", priceCurrency: "USD" } }}
      />
      <FeaturePageLayout
        badge="Revenue" badgeIcon={<DollarSign size={13} className="text-coral" />}
        heroTitle={<>See Exactly How Much<br /><em className="text-coral not-italic">Each Email Earns</em></>}
        heroSubtitle="Track conversions and revenue from every email campaign. Automatic attribution from Shopify, Stripe, and custom integrations — with a real-time revenue dashboard."
        stats={[{ value: "4", label: "Revenue sources" }, { value: "Real-time", label: "Dashboard updates" }, { value: "$0", label: "Extra cost" }]}
        demo={<TypewriterDemo scenarios={SCENARIOS} title="Revenue Attribution Engine" buttonLabel="Track" />}
        howTitle="Connect Your Revenue Sources"
        howDesc="Plug in Shopify, Stripe, or use the API — revenue flows in automatically."
        howSteps={[
          { title: "Connect a Revenue Source", desc: "Shopify order webhooks, Stripe payment events, REST API, or manual entry. Pick what fits your stack." },
          { title: "Automatic Attribution", desc: "MoreMail matches each conversion to the email or automation that drove it, using click tracking and subscriber history." },
          { title: "Revenue Dashboard", desc: "See total revenue, per-campaign revenue, conversion rates, and daily trends in real time." },
        ]}
        benefitsTitle="Know Your Email ROI"
        benefits={[
          { icon: <ShoppingBag size={18} className="text-coral" />, title: "Shopify Integration", desc: "Order webhooks automatically attribute revenue to the email campaign that drove the purchase." },
          { icon: <CreditCard size={18} className="text-coral" />, title: "Stripe Payments", desc: "Track subscription payments, one-time charges, and recurring revenue from Stripe events." },
          { icon: <Code size={18} className="text-coral" />, title: "REST API", desc: "Record conversions programmatically from any platform with a simple POST request." },
          { icon: <BarChart3 size={18} className="text-coral" />, title: "Revenue Dashboard", desc: "Total revenue, per-campaign breakdown, daily trends, and conversion rates in one view." },
          { icon: <TrendingUp size={18} className="text-coral" />, title: "Per-Campaign ROI", desc: "See exactly which campaigns are profitable and which need optimization." },
          { icon: <Target size={18} className="text-coral" />, title: "Auto-Deduplication", desc: "External IDs prevent double-counting. Every conversion is recorded exactly once." },
        ]}
        ctaHeadline="Stop Flying Blind on Revenue"
        ctaDescription="Connect your revenue source in minutes. See exactly how much every email earns."
        currentPath="/revenue-attribution"
      >
        {/* API endpoints + dashboard details */}
        <section className="py-12 bg-white">
          <div className="max-w-3xl mx-auto px-6">
            <h2 className="font-display text-2xl font-bold text-ink text-center mb-3">
              What the Dashboard <span className="text-coral">Shows You</span>
            </h2>
            <p className="text-sm text-slate text-center mb-8 max-w-lg mx-auto">
              The revenue summary endpoint powers a complete analytics view. Here's what you see at a glance.
            </p>
            <div className="space-y-3">
              {[
                { label: "Total Revenue & Conversions", desc: "Sum of all revenue and conversion count in your chosen time window (up to 365 days)." },
                { label: "Unique Converters", desc: "How many distinct subscribers have converted — not just how many times." },
                { label: "Revenue by Campaign (Top 10)", desc: "Which campaigns drove the most revenue, ranked by dollar amount with campaign names." },
                { label: "Revenue by Event Type", desc: "Breakdown by event name — purchases, subscriptions, upgrades, custom events." },
                { label: "Daily Revenue Trend", desc: "Day-by-day revenue chart so you can spot patterns, spikes, and dips." },
                { label: "Per-Campaign Detail", desc: "Drill into any campaign to see its conversions, revenue, and which subscribers converted." },
              ].map(item => (
                <div key={item.label} className="flex items-start gap-3 bg-paper rounded-xl p-4 border border-sand">
                  <CheckCircle size={14} className="text-teal flex-shrink-0 mt-0.5" />
                  <div>
                    <span className="text-sm font-bold text-ink">{item.label}</span>
                    <p className="text-xs text-slate mt-0.5">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </FeaturePageLayout>
    </>
  );
}
